/**
 * Permission Engine - Main Class
 *
 * Core permission calculation engine with modular evaluation
 */

import { systemLogger } from '@/lib/logging';
import {
  UserContext,
  PermissionRequirement,
  PermissionResult,
  ProjectMembership,
  CompanyContract,
  PERMISSION_CONFIG
} from '../types';
import { validateInputs, isSystemAdmin, createSuccessResult } from './validation';
import { evaluateResourcePermission } from './resource-evaluators';

/**
 * Core permission calculation engine
 */
export class PermissionEngine {
  private debugMode: boolean;

  constructor(debugMode: boolean = PERMISSION_CONFIG.DEBUG_MODE) {
    this.debugMode = debugMode;
  }

  /**
   * Main permission evaluation method
   */
  async evaluatePermission(
    user: UserContext,
    requirement: PermissionRequirement,
    projectMemberships: ProjectMembership[] = [],
    companyContracts: CompanyContract[] = []
  ): Promise<PermissionResult> {
    const startTime = performance.now();

    try {
      // Step 1: Validate inputs
      const validationResult = validateInputs(user, requirement);
      if (!validationResult.allowed) {
        return validationResult;
      }

      // Step 2: System Admin bypass - grant all permissions
      if (isSystemAdmin(user)) {
        if (this.debugMode) {
          systemLogger.debug('System Admin bypass granted permission', {
            user: user.id,
            resource: requirement.resource,
            action: requirement.action
          });
        }
        return createSuccessResult(
          'SYSTEM_ADMIN_BYPASS',
          'System Admin has unrestricted access'
        );
      }

      // Step 3: Evaluate resource permission
      const result = await evaluateResourcePermission(
        user,
        requirement,
        projectMemberships,
        companyContracts
      );

      // Step 4: Log performance metrics
      const duration = performance.now() - startTime;
      if (this.debugMode) {
        systemLogger.debug('Permission evaluation completed', {
          user: user.id,
          resource: requirement.resource,
          action: requirement.action,
          allowed: result.allowed,
          duration: `${duration.toFixed(2)}ms`
        });
      }

      return result;
    } catch (error) {
      systemLogger.error('Permission evaluation failed', error);
      return {
        allowed: false,
        reason: 'Permission evaluation error'
      };
    }
  }
}